/**
 * Доска задач разработки (03.11.2026): задачи чатов Claude и людей команды портала — в самом портале, а не в
 * артефакте claude.ai. Доступ — владелец портала и люди, которых он добавил; чаты работают ключом доски, который
 * подставляет прокси claude.ai/code. Взять задачу можно только свободную: кто взял и когда — видно всем.
 *
 * Это данные портала, а не компании: в демо-компании и у заказчиков доски нет.
 */
import { z } from 'zod';

export const BOARD_STATUSES = ['new', 'doing', 'review', 'blocked', 'done', 'cancelled'] as const;
export type BoardStatus = (typeof BOARD_STATUSES)[number];

/** «Открытые»: всё, что ещё не выложено и не отменено */
export const BOARD_OPEN_STATUSES: readonly BoardStatus[] = ['new', 'doing', 'review', 'blocked'];
export const BOARD_CLOSED_STATUSES: readonly BoardStatus[] = ['done', 'cancelled'];

export const BOARD_STATUS_TITLES: Record<BoardStatus, string> = {
  new: 'новая',
  doing: 'в работе',
  review: 'ждёт выкладки',
  blocked: 'ждёт',
  done: 'выложено',
  cancelled: 'отменена',
};

export const BOARD_PRIOS = ['П0', 'П1', 'П2', 'П3'] as const;
export type BoardPrio = (typeof BOARD_PRIOS)[number];

/** Что значит приоритет (docs/chats/main.md, решение владельца 28.10.2026) */
export const BOARD_PRIO_HINTS: Record<BoardPrio, string> = {
  П0: 'привести в порядок: выкладка, документы',
  П1: 'держит пилот',
  П2: 'до приёмки первой очереди',
  П3: 'позже или ждёт внешнего',
};

export const BOARD_NOTE_KINDS = ['comment', 'instruction'] as const;
export type BoardNoteKind = (typeof BOARD_NOTE_KINDS)[number];
export const BOARD_NOTE_KIND_TITLES: Record<BoardNoteKind, string> = {
  comment: 'комментарий',
  instruction: 'поручение чату',
};

export const BOARD_CHAT_STATES = ['active', 'waiting', 'paused'] as const;
export type BoardChatState = (typeof BOARD_CHAT_STATES)[number];
export const BOARD_CHAT_STATE_TITLES: Record<BoardChatState, string> = {
  active: 'работает',
  waiting: 'ждёт',
  paused: 'на паузе',
};

/** Код чата: main, plan, help, common, est, proc, site, fin, joints, ai, later, owner, board… */
export const BOARD_CHAT_RE = /^[a-z0-9_-]{1,32}$/;
export const BOARD_CHAT_MESSAGE = 'Код чата — от 1 до 32 латинских строчных букв, цифр, «-» или «_»';

/** Ключ доски: «epcb_» и 32 случайных байта; в базе — только его отпечаток SHA-256 */
export const BOARD_KEY_PREFIX = 'epcb_';
/**
 * Заголовки запросов чатов. Ключ — в `X-Board-Key` без префикса: его подставляет прокси claude.ai/code («Network
 * secrets»), на компьютере — `BOARD_KEY` у `scripts/board.mjs`. `Authorization: Bearer` тоже принимается, но этот
 * заголовок портал читает как вход: cookie-вход из такого окружения перестаёт работать. Код чата — `X-Board-Chat`,
 * номер запуска чата — `X-Board-Run` (`scripts/board.mjs` создаёт его один раз на контейнер).
 */
export const BOARD_KEY_HEADER = 'x-board-key';
export const BOARD_CHAT_HEADER = 'x-board-chat';
export const BOARD_RUN_HEADER = 'x-board-run';
export const BOARD_RUN_RE = /^[A-Za-z0-9_-]{8,64}$/;
export const BOARD_RUN_MESSAGE = 'Номер запуска — от 8 до 64 латинских букв, цифр, «-» или «_»';
/** Главный чат правит строки всех чатов и отпускает чужие задачи (как на прежней доске) */
export const BOARD_MAIN_CHAT = 'main';

export const BoardChatCode = z.string().trim().toLowerCase().regex(BOARD_CHAT_RE, BOARD_CHAT_MESSAGE);

const Text = (max: number) => z.string().trim().max(max);

/** Кто я на доске: человек, владелец ли портала, от имени какого чата и какого запуска чата (для ключа) */
export const BoardMe = z.object({
  userId: z.uuid(),
  fullName: z.string(),
  isOwner: z.boolean(),
  chat: z.string().nullable(),
  run: z.string().nullable(),
  via: z.enum(['session', 'key']),
});
export type BoardMe = z.infer<typeof BoardMe>;

export const BoardTask = z.object({
  id: z.uuid(),
  /** Номер задачи на прежней доске (артефакт), если задача перенесена оттуда */
  ref: z.string().nullable(),
  title: z.string(),
  chat: z.string().nullable(),
  prio: z.enum(BOARD_PRIOS),
  rank: z.number().int(),
  status: z.enum(BOARD_STATUSES),
  reqs: z.array(z.string()),
  details: z.string().nullable(),
  blocker: z.string().nullable(),
  branch: z.string().nullable(),
  /** Кто ведёт со стороны компании */
  leadUserId: z.uuid().nullable(),
  leadName: z.string().nullable(),
  /** Кто взял: человек (у чата — владелец ключа) и чат */
  takenByUserId: z.uuid().nullable(),
  takenByName: z.string().nullable(),
  takenByChat: z.string().nullable(),
  takenAt: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdByChat: z.string().nullable(),
  createdAt: z.string(),
  updatedByName: z.string().nullable(),
  updatedByChat: z.string().nullable(),
  updatedAt: z.string(),
  /** Номер версии: правка с устаревшим номером получает отказ 409 «stale» */
  version: z.number().int(),
  notesCount: z.number().int(),
});
export type BoardTask = z.infer<typeof BoardTask>;

export const BoardNote = z.object({
  id: z.uuid(),
  taskId: z.uuid(),
  kind: z.enum(BOARD_NOTE_KINDS),
  text: z.string(),
  authorUserId: z.uuid().nullable(),
  authorName: z.string().nullable(),
  authorChat: z.string().nullable(),
  createdAt: z.string(),
});
export type BoardNote = z.infer<typeof BoardNote>;

export const BoardTaskDetail = BoardTask.extend({ notes: z.array(BoardNote) });
export type BoardTaskDetail = z.infer<typeof BoardTaskDetail>;

/**
 * Ответ «Взять». alreadyYours — задача уже была за этим же взявшим: человеком на странице или тем же человеком, чатом
 * и запуском чата; ничего не изменилось, takenAt — с какого времени она его.
 */
export const BoardTakeResult = BoardTaskDetail.extend({ alreadyYours: z.boolean() });
export type BoardTakeResult = z.infer<typeof BoardTakeResult>;

const Reqs = z.array(z.string().trim().min(1).max(40)).max(50);

export const BoardTaskInput = z.object({
  title: z.string().trim().min(1, 'Напишите, что сделать').max(500),
  chat: BoardChatCode.nullable().optional(),
  prio: z.enum(BOARD_PRIOS).optional(),
  rank: z.number().int().min(-1_000_000).max(1_000_000).optional(),
  /** Новая задача — «новая» или «ждёт» (вопрос владельцу); «новая» становится «в работе», когда её берут */
  status: z.enum(['new', 'blocked']).optional(),
  reqs: Reqs.optional(),
  details: Text(20_000).nullable().optional(),
  blocker: Text(2_000).nullable().optional(),
  branch: Text(200).nullable().optional(),
  leadUserId: z.uuid().nullable().optional(),
});
export type BoardTaskInput = z.infer<typeof BoardTaskInput>;

export const BoardTaskPatch = z.object({
  title: z.string().trim().min(1, 'Напишите, что сделать').max(500).optional(),
  chat: BoardChatCode.nullable().optional(),
  prio: z.enum(BOARD_PRIOS).optional(),
  rank: z.number().int().min(-1_000_000).max(1_000_000).optional(),
  status: z.enum(BOARD_STATUSES).optional(),
  reqs: Reqs.optional(),
  details: Text(20_000).nullable().optional(),
  blocker: Text(2_000).nullable().optional(),
  branch: Text(200).nullable().optional(),
  leadUserId: z.uuid().nullable().optional(),
  /** Версия, которую видел правящий; не совпала — отказ 409 «stale» */
  version: z.number().int().optional(),
});
export type BoardTaskPatch = z.infer<typeof BoardTaskPatch>;

/** «Отпустить»: какой поставить статус (по умолчанию «в работе» → «новая», «ждёт» остаётся «ждёт») */
export const BoardReleaseInput = z.object({ status: z.enum(BOARD_STATUSES).optional() });
export type BoardReleaseInput = z.infer<typeof BoardReleaseInput>;

/** Поручение (instruction) пишет человек на странице доски или главный чат; остальные чаты — комментарии */
export const BoardNoteInput = z.object({
  kind: z.enum(BOARD_NOTE_KINDS).default('comment'),
  text: z.string().trim().min(1, 'Напишите текст').max(20_000),
});
export type BoardNoteInput = z.infer<typeof BoardNoteInput>;

const SINCE_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2}))?$/;
export const BOARD_SINCE_MESSAGE =
  'Отбор «с какого времени» — дата 2026-11-03 (с начала дня по времени компании) или время 2026-11-03T05:00:00Z';

/** Дата ГГГГ-ММ-ДД или время ISO с поясом (Z или +05:00), год 2000–2999 и настоящий день месяца */
export function boardSinceValid(v: string): boolean {
  const m = SINCE_RE.exec(v);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < 2000 || y > 2999) return false;
  const day = new Date(Date.UTC(y, mo - 1, d));
  if (day.getUTCMonth() !== mo - 1 || day.getUTCDate() !== d) return false;
  if (m[4] === undefined) return true;
  if (Number(m[4]) > 23 || Number(m[5]) > 59 || Number(m[6] ?? 0) > 59) return false;
  // Пояс вроде +99:99 регулярное выражение пропускает, а время из него не получается
  return Number.isFinite(Date.parse(v));
}

/** Список задач больше этого числа строк за раз не отдаёт */
export const BOARD_LIST_MAX = 1000;

/** Список задач: статус open (по умолчанию), all или список через запятую; «кто взял» — any, free, mine */
export const BOARD_TAKEN_FILTERS = ['any', 'free', 'mine'] as const;
export type BoardTakenFilter = (typeof BOARD_TAKEN_FILTERS)[number];
export const BoardTaskQuery = z.object({
  chat: z.string().trim().max(32).optional(),
  status: z.string().trim().max(200).optional(),
  taken: z.enum(BOARD_TAKEN_FILTERS).default('any'),
  q: z.string().trim().max(200).optional(),
  /** Созданные с этого времени (дата — с начала дня по времени компании) */
  since: z.string().trim().refine(boardSinceValid, BOARD_SINCE_MESSAGE).optional(),
  limit: z.coerce.number().int().min(1).max(BOARD_LIST_MAX).default(500),
});
export type BoardTaskQuery = z.infer<typeof BoardTaskQuery>;

/** Задачи по отбору: total — сколько подходит всего, truncated — показаны не все (не больше limit) */
export const BoardTaskList = z.object({
  tasks: z.array(BoardTask),
  total: z.number().int(),
  truncated: z.boolean(),
});
export type BoardTaskList = z.infer<typeof BoardTaskList>;

export const BoardChat = z.object({
  code: z.string(),
  name: z.string().nullable(),
  area: z.string().nullable(),
  branch: z.string().nullable(),
  state: z.enum(BOARD_CHAT_STATES),
  statusLine: z.string().nullable(),
  lastRun: z.string().nullable(),
  next: z.string().nullable(),
  order: z.number().int(),
  updatedAt: z.string(),
  updatedByName: z.string().nullable(),
  updatedByChat: z.string().nullable(),
});
export type BoardChat = z.infer<typeof BoardChat>;

export const BoardChatInput = z.object({
  name: Text(200).nullable().optional(),
  area: Text(500).nullable().optional(),
  branch: Text(200).nullable().optional(),
  state: z.enum(BOARD_CHAT_STATES).optional(),
  statusLine: Text(2_000).nullable().optional(),
  lastRun: z.iso.datetime({ offset: true }).nullable().optional(),
  next: Text(2_000).nullable().optional(),
  order: z.number().int().min(-100_000).max(100_000).optional(),
});
export type BoardChatInput = z.infer<typeof BoardChatInput>;

export const BoardKey = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  userName: z.string(),
  name: z.string(),
  /** Начало ключа — чтобы узнать его в настройках окружения; сам ключ портал больше не показывает */
  prefix: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type BoardKey = z.infer<typeof BoardKey>;

export const BoardKeyInput = z.object({
  name: z.string().trim().min(1, 'Назовите ключ: где он будет работать').max(100),
});
export type BoardKeyInput = z.infer<typeof BoardKeyInput>;

/** Новый ключ: сам ключ — только в этом ответе, один раз */
export const BoardKeyCreated = BoardKey.extend({ key: z.string() });
export type BoardKeyCreated = z.infer<typeof BoardKeyCreated>;

export const BoardMember = z.object({
  userId: z.uuid(),
  fullName: z.string(),
  /** Почта — только владельцу портала на странице доски; остальным (и любому ключу) — null */
  email: z.string().nullable(),
  /** Владелец портала: доступ к доске всегда, убрать нельзя */
  isOwner: z.boolean(),
  isActive: z.boolean(),
  addedAt: z.string().nullable(),
  addedByName: z.string().nullable(),
});
export type BoardMember = z.infer<typeof BoardMember>;

export const BoardMemberInput = z.object({
  email: z.email('Неверный адрес почты').transform((s) => s.toLowerCase()),
});
export type BoardMemberInput = z.infer<typeof BoardMemberInput>;

/** Перенос с прежней доски: сколько записей принято и сколько пропущено (и почему — первые причины) */
export const BoardImportResult = z.object({
  tasks: z.number().int(),
  notes: z.number().int(),
  chats: z.number().int(),
  skipped: z.number().int(),
  created: z.object({ tasks: z.number().int(), notes: z.number().int(), chats: z.number().int() }),
  problems: z.array(z.string()),
});
export type BoardImportResult = z.infer<typeof BoardImportResult>;

/** «Задачу уже взял Сергей Иванов (чат plan) 03.11 10:42» — одинаково на сервере и в подсказках */
export function boardTakerLabel(t: { takenByName: string | null; takenByChat: string | null }): string {
  const who = t.takenByName ?? 'человек, которого уже нет в портале';
  return t.takenByChat ? `${who} (чат ${t.takenByChat})` : who;
}
